// src/utils/bibAdapter.ts
// BIB_SIMPLE のネストレスポンスから各種表示用データを抽出するアダプタ

import { BibSimpleResponse } from '../types/rlTypes';
import { buildOgpUrlByRlBookId, buildOgpUrlByTitle } from '../config/api';

/**
 * BibSimpleResponse からタイトルを抽出
 */
export const getBibTitle = (bib: BibSimpleResponse): string => {
  const ndl = bib.sources.ndl;
  if (ndl) return ndl.title;
  const hanmoto = bib.sources.hanmoto;
  if (hanmoto?.title) return hanmoto.title;
  const ol = bib.sources.ol;
  if (ol?.title) return ol.title;
  return '';
};

/**
 * BibSimpleResponse から ISBN13 を抽出
 */
export const getBibIsbn = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.isbn13
    ?? bib.sources.hanmoto?.isbn13
    ?? bib.sources.ol?.isbn13;
};

/**
 * BibSimpleResponse から出版社を抽出
 */
export const getBibPublisher = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.publisher
    ?? bib.sources.hanmoto?.publisher
    ?? bib.sources.ol?.publisher;
};

/**
 * BibSimpleResponse から出版日を抽出
 */
export const getBibPublishingDate = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.publishing_date
    ?? bib.sources.hanmoto?.publishing_date
    ?? bib.sources.ol?.publishing_date;
};

/**
 * BibSimpleResponse から責任表示（著者表記）を抽出
 */
export const getBibResponsibility = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.responsibility;
};

/**
 * BibSimpleResponse から NDL 書誌 ID を抽出（NDLリンク用）
 */
export const getBibNdlId = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.ndl_bib_id;
};

/**
 * BibSimpleResponse から巻次（245$n + $p）を抽出。漫画等のシリーズ物のみ値を持つ
 */
export const getBibVolume = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.volume;
};

/**
 * タイトルに巻数があれば付記する（例: "正直不動産" + "15" -> "正直不動産（15）"）。
 * GraphPage.tsx等の一覧表示と同じ表記規則
 */
export const titleWithVolume = (title: string, volume?: string | number | null): string => {
  const volumeStr = volume != null ? String(volume).trim() : '';
  return volumeStr ? `${title}（${volumeStr}）` : title;
};

/**
 * BibSimpleResponse からサブタイトル情報を抽出
 */
export const getBibContentDetailTitle = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.content_detail_title;
};

/**
 * BibSimpleResponse からその他タイトルを抽出
 */
export const getBibTitleOthers = (bib: BibSimpleResponse): string | undefined => {
  return bib.sources.ndl?.title_others;
};

/**
 * BibSimpleResponse から contributors 配列を取得
 */
export const getBibContributors = (bib: BibSimpleResponse) => {
  return bib.sources.ndl?.contributors ?? [];
};

/**
 * BibSimpleResponse からカバー画像URLを生成
 * rl_book_id があればそれを使い、なければ title + isbn にフォールバック
 */
export const generateCoverUrlFromBib = (bib: BibSimpleResponse): string => {
  if (bib.rl_book_id) {
    return buildOgpUrlByRlBookId(bib.rl_book_id);
  }
  const title = getBibTitle(bib);
  const isbn = getBibIsbn(bib);
  return buildOgpUrlByTitle(title, isbn);
};
